// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import React, { FC, useCallback } from "react";
/* eslint-disable spire/fenced-imports */
import { useSwiper } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import { useArrowElement } from "@ces/App/Components/XngCarousel/hooks";
import { ArrowPosition, ArrowType, NavigationProps } from "@ces/App/Components/XngCarousel/models";
import LeftNav from "@ces/XDL/Icons/LeftNav";
import RightNav from "@ces/XDL/Icons/RightNav";
import { color, xClassNames } from "@ces/XDL/Utilities";
import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

const generateArrowStyle = (arrowPosition: ArrowPosition) => {
    return {
        size: 16,
        css: css`
            ${arrowPosition === "left" ? `margin-right` : `margin-left`}: 12px;
            background: transparent;
            height: 8px;
            width: 14px;

            & > ${IconWrapper} {
                color: ${color("primary")};
                transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
            }
            &:hover {
                & > ${IconWrapper} {
                    color: ${color("common.ui8", 1)};
                }
            }
            transform: none;
            z-index: 10;
            padding: 0;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            border: 0;
            outline: none !important;

            &:focus {
                box-shadow: none;
            }

            ${(arrowPosition === "top" || arrowPosition === "bottom") &&
            css`
                position: relative;
                inset: 0;
                transform: none;
                margin: auto;
            `};

            &.swiper-button-disabled {
                cursor: not-allowed;
                & > ${IconWrapper} {
                    color: ${color("text.main", 0.33)};
                }
            }
        `,
    };
};

const arrowNavigationIcons = {
    top: {
        src: ArrowUp,
        size: 14,
    },
    bottom: {
        src: ArrowDown,
        size: 14,
    },
    left: {
        src: LeftNav,
        size: 14,
    },
    right: {
        src: RightNav,
        size: 14,
    },
};

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    return isVertical ? "top" : "left";
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile, buttonIconProps, ...otherProps }) => {
        const swiperInstance = useSwiper();
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
        const { src: iconSrc, size: iconSize } = arrowNavigationIcons[arrowPosition];

        const {
            className,
            element: arrowElement,
            disabled,
        } = useArrowElement({
            swiper: swiperInstance,
            showOnMobile,
            arrowType,
        });

        const onClickHandler = useCallback(
            (event: any) => {
                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [arrowElement],
        );

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            arrowPosition,
            showOnMobile,
            className: xClassNames.append(className, otherProps.className),
        };

        return (
            <Clickable
                {...props}
                {...generateArrowStyle(arrowPosition)}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClickHandler}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon ${disabled && "disabled"}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

export default ArrowNavigation;
